import { _createFieldConfig } from "./fieldConfig";
import { toCamelCase } from "../../Payroll/helper-payroll/HelperFunction";
export const _hasNegativeValues = (data) => {
  return data.some((entry) =>
    Object.values(entry).some((value) => {
      if (typeof value === "number") {
        return value < 0;
      }
      if (Array.isArray(value)) {
        return value.some((item) => typeof item === "number" && item < 0);
      }
      return false;
    })
  );
};

export const _hasNetBelow5k = (payrollData = []) => {
  if (!Array.isArray(payrollData) || payrollData.length === 0) return false;

  const toNum = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number" && Number.isFinite(v)) return v;
    const n = parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  };

  return payrollData.some((row) => {
    const net = row?.netAmount;
    if (Array.isArray(net)) {
      return net.some((x) => {
        const n = toNum(x);
        return n != null && n < 5000;
      });
    }
    const n = toNum(net);
    return n != null && n < 5000;
  });
}

export const _getInsuredDaysAndMonths = (item) => {
  if (!item || !Array.isArray(item.selectedDate))
    return { daysInAMonth: [], gsisInsuredDays: [] };
  const daysArr = [];
  const gsisArr = [];
  for (let i = 0; i < item.selectedDate.length; i++) {
    const monthYear = item.selectedDate[i].replace(" ", " - ");
    const days =
      Array.isArray(item.daysInAMonth) && item.daysInAMonth[i] !== undefined
        ? item.daysInAMonth[i]
        : "";
    const gsis =
      Array.isArray(item.gsisInsuredDays) &&
      item.gsisInsuredDays[i] !== undefined
        ? item.gsisInsuredDays[i]
        : "";
    daysArr.push(`${monthYear}: ${days}`);
    gsisArr.push(`${monthYear}: ${gsis}`);
  }
  return { daysInAmonth: daysArr, gsisInsuredDays: gsisArr };
};

export const _buildEmployeeMap = (employees = [], keysToInclude = []) => {
  return employees.reduce((acc, curr) => {
    acc[curr.id] = keysToInclude.reduce((obj, key) => {
      obj[key] = curr?.[key] || [];
      return obj;
    }, {});
    return acc;
  }, {});
};

export const _formatDateToMonthYear = (rawDate) => {
  if (!rawDate) return "";

  const dateObj = new Date(rawDate);
  if (isNaN(dateObj)) return "";

  const month = dateObj.toLocaleString("en-US", { month: "long" });
  const year = dateObj.getFullYear();

  return `${month} - ${year}`;
};

const MONTH_NAME_TO_INDEX = {
  january: 0,
  february: 1,
  march: 2,
  april: 3,
  may: 4,
  june: 5,
  july: 6,
  august: 7,
  september: 8,
  october: 9,
  november: 10,
  december: 11,
};

const getMonthIndexFromName = (monthName) => {
  if (!monthName || typeof monthName !== "string") return -1;
  return MONTH_NAME_TO_INDEX[monthName.trim().toLowerCase()] ?? -1;
};

export const formatMonthPeriodRange = (startDate, endDate, year) => {
  const yearNum = parseInt(String(year), 10);
  const startMonthIndex = getMonthIndexFromName(startDate);
  const endMonthIndex = getMonthIndexFromName(endDate);

  if (!yearNum || startMonthIndex === -1 || endMonthIndex === -1) {
    return `${startDate ?? ""}-${endDate ?? ""} ${year ?? ""}`.trim();
  }

  const lastDayOfEndMonth = new Date(yearNum, endMonthIndex + 1, 0).getDate();

  return `${startDate.trim()} 1 - ${endDate.trim()} ${lastDayOfEndMonth}, ${yearNum}`;
};

export const _getArrayofDates = (item) => {
  if (Array.isArray(item?.selectedDate) && item.selectedDate.length > 0) {
    return item.selectedDate.map((d) => d.replace(" ", " - "));
  }
  return [];
};

const getLabeledEntryKey = (value) => {
  if (typeof value !== "string") return null;
  const idx = value.lastIndexOf(":");
  if (idx === -1) return null;
  return value.slice(0, idx).trim();
};

/** Replace same-period labeled entries instead of appending duplicates. */
export const upsertLabeledValues = (collection, value) => {
  if (Array.isArray(value)) {
    for (const item of value) {
      upsertLabeledValues(collection, item);
    }
    return;
  }

  const label = getLabeledEntryKey(value);
  if (label == null) {
    collection.push(value);
    return;
  }

  for (let i = collection.length - 1; i >= 0; i--) {
    if (getLabeledEntryKey(collection[i]) === label) {
      collection.splice(i, 1);
    }
  }
  collection.push(value);
};

export const _areArraysEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export const _getProcessItems = ({
  data,
  isBackpay,
  objectData,
  concattedMonthYear,
  employeeMap,
}) => {
  const { payrollType } = objectData;
  const result = {};

  const fieldConfig = _createFieldConfig({
    isBackpay,
    objectData,
    concattedMonthYear,
  });

  const taxKeys = Object.keys(fieldConfig).concat([
    "basicTax",
    "apTax",
    "individualTax",
  ]);

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    const existingArray = employeeMap[item?.employee?.id] ?? {};
    const arrayOfDates = _getArrayofDates(item);

    const updatedArray = Object.fromEntries(
      taxKeys.map((key) => [key, [...(existingArray[key] || [])]])
    );

    for (const [field, computeFn] of Object.entries(fieldConfig)) {
      if (field === "payrollTax") continue;
      upsertLabeledValues(updatedArray[field], computeFn(item, arrayOfDates));
    }

    switch (payrollType) {
      case "Basic Pay":
        upsertLabeledValues(
          updatedArray.basicTax,
          fieldConfig.payrollTax(item, arrayOfDates)
        );
        break;
      case "Augmentation Pay":
        upsertLabeledValues(
          updatedArray.apTax,
          fieldConfig.payrollTax(item, arrayOfDates)
        );
        break;
      case "Individual Payroll":
        upsertLabeledValues(
          updatedArray.individualTax,
          fieldConfig.payrollTax(item, arrayOfDates)
        );
        break;
      default:
        break;
    }

    result[item.employee.id] = { ...updatedArray };
  }

  return result;
};

export const _getAccountsTitle = ({ payrollItems, accountsTitle }) => {
  const allDeductions = payrollItems.flatMap(
    (entry) => entry?.deductions || []
  );
  const deductionOrderMap = {};

  allDeductions.forEach((deduction, index) => {
    const [rawKey] = deduction.split(":");
    const key = rawKey?.trim();

    if (key && deductionOrderMap[key] === undefined) {
      deductionOrderMap[key] = index;
    }
  });

  const deductionTotals = allDeductions.reduce((acc, deduction) => {
    const [rawKey, rawValue] = deduction.split(":");
    const key = rawKey.trim();
    const value = parseFloat(rawValue?.trim() || "0");
    acc[key] = (acc[key] || 0) + value;
    return acc;
  }, {});


  return accountsTitle
    .filter((account) => deductionTotals[toCamelCase(account?.name)])
    .map((account) => ({
      name: account?.name,
      uacsCode: account?.uacsCode,
      id: account?.id,
      amount: deductionTotals[toCamelCase(account?.name)] || 0,
    }))
    .sort((a, b) => {
      const aKey = toCamelCase(a.name);
      const bKey = toCamelCase(b.name);
      const aIndex =
        deductionOrderMap[aKey] !== undefined
          ? deductionOrderMap[aKey]
          : Number.MAX_SAFE_INTEGER;
      const bIndex =
        deductionOrderMap[bKey] !== undefined
          ? deductionOrderMap[bKey]
          : Number.MAX_SAFE_INTEGER;

      return aIndex - bIndex;
    });
};

export const _classifyDebitCredit = (titleAccLists, incentiveType) => {
  const debitIds = new Set(
    incentiveType?.otherAdditionals?.map((item) => item.id) || []
  );
  const creditIds = new Set(
    incentiveType?.otherDeductions?.map((item) => item.id) || []
  );

  const result = {
    debit: [],
    credit: [],
  };

  for (const item of titleAccLists) {
    if (debitIds.has(item.id)) {
      result.debit.push(item);
    } else if (creditIds.has(item.id)) {
      result.credit.push(item);
    }
  }

  return result;
};

export const _sumAmounts = (str) => {
  let values = [];

  if (Array.isArray(str)) {
    values = str;
  } else if (typeof str === "string") {
    values = str.split(",");
  } else {
    return 0;
  }

  return values
    .map((v) => parseFloat(v))
    .filter((n) => !isNaN(n))
    .reduce((a, b) => a + b, 0);
};

export const _calculateTax = (payroll, payItem, checkCase) => {
  let taxTotal = 0;

  const { tax, totalTax, threePercent } = payroll;

  if (Array.isArray(tax)) {
    taxTotal += _sumAmounts(tax);
  } else if (totalTax) {
    taxTotal += totalTax;
  } else if (!isNaN(tax)) {
    taxTotal += tax;
  } else if (
    checkCase("isIndividualJoBackpay") ||
    checkCase("isIndividualJoInitial")
  ) {
    taxTotal += threePercent || 0;
  }

  if (payItem === "20% Monthly Premium") {
    taxTotal += threePercent || 0;
  }

  return taxTotal;
};

const EDUCATION_LEVEL_RANK = {
  Elementary: 0,
  Secondary: 1,
  "Vocational/Trade Course": 2,
  College: 3,
  "Graduate Studies": 4,
};

export const getHighestEducationalBackground = (records = []) => {
  if (!Array.isArray(records) || records.length === 0) return undefined;

  return records.reduce((best, current) => {
    if (!best) return current;

    const bestRank = EDUCATION_LEVEL_RANK[best?.educationLevel] ?? -1;
    const currentRank = EDUCATION_LEVEL_RANK[current?.educationLevel] ?? -1;

    if (currentRank > bestRank) return current;
    if (currentRank < bestRank) return best;

    const bestCreated = new Date(best?.createdAt).getTime() || 0;
    const currentCreated = new Date(current?.createdAt).getTime() || 0;

    return currentCreated > bestCreated ? current : best;
  });
};
